/**
 * Apps API Client
 *
 * Browser helpers for Losi Apps API calls.
 *
 * Primary responsibilities:
 * - Normalize fetch errors
 * - Keep component API calls small and typed
 *
 * Dependencies:
 * - App detail types
 */

import type { AppDefinition } from '@/lib/apps/definition'
import type { AppDetail, AppPreviewDraftState, AppRow } from '@/types/losi-apps'

export interface AppDraftResponse {
  definition: AppDefinition
  explanation: string
  templateId: string
  usedAi: boolean
}

export interface AppsAccessResponse {
  betaEnabled?: boolean
  canAccessApps?: boolean
  canCreateApps?: boolean
}

// --------------

export async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)

  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(input, {
    ...init,
    headers
  })

  if (!response.ok) {
    throw new Error(await readResponseError(response))
  }

  return (await response.json()) as T
}

// --------------

export async function loadApps(workspaceId: string) {
  return fetchJson<{ apps: AppRow[] }>(`/api/apps?workspaceId=${encodeURIComponent(workspaceId)}`)
}

// --------------

export async function loadAppsAccess(workspaceId?: string | null) {
  const params = new URLSearchParams()

  if (workspaceId) {
    params.set('workspaceId', workspaceId)
  }

  return fetchJson<AppsAccessResponse>(
    `/api/apps/access${params.size > 0 ? `?${params.toString()}` : ''}`,
    {
      cache: 'no-store'
    }
  )
}

// --------------

export async function createAppDraft(params: {
  prompt: string
  templateId?: string | null
  workspaceId: string
}) {
  return fetchJson<AppDraftResponse>('/api/apps/create-draft', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

// --------------

export async function editAppDraft(params: {
  definition: AppDefinition
  instruction: string
  workspaceId: string
}) {
  return fetchJson<{
    actionInput?: Record<string, unknown>
    actionKey?: string | null
    definition: AppDefinition
    intent?: 'run_request' | 'structural_change'
    summary: string
  }>('/api/apps/edit-draft', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

// --------------

export async function runAppPreviewAction(params: {
  actionKey: string
  definition: AppDefinition
  input?: Record<string, unknown>
  setupValues: Record<string, string>
  sourceImageDataUrls?: string[]
  sourceText?: string
  workspaceId: string
}) {
  return fetchJson<{
    previewRun: AppPreviewDraftState['previewRun']
    sourceImageDataUrls?: string[]
  }>(
    '/api/apps/preview-action',
    {
      method: 'POST',
      body: JSON.stringify(params)
    }
  )
}

// --------------

export async function createApp(params: {
  definition: AppDefinition
  previewState?: AppPreviewDraftState | null
  visibility?: 'private' | 'workspace'
  workspaceId: string
}) {
  return fetchJson<{ app: AppRow }>('/api/apps', {
    method: 'POST',
    body: JSON.stringify(params)
  })
}

// --------------

export async function loadAppDetail(appId: string) {
  return fetchJson<AppDetail>(`/api/apps/${appId}`)
}

// --------------

async function readResponseError(response: Response) {
  const text = await response.text().catch(() => '')

  if (!text.trim()) {
    return 'Request failed'
  }

  try {
    const payload = JSON.parse(text) as unknown

    if (
      payload &&
      typeof payload === 'object' &&
      'error' in payload &&
      typeof payload.error === 'string'
    ) {
      return payload.error
    }
  } catch {
    return text
  }

  return text
}
